// apps/academy/src/hooks/useCourses.ts
import { searchCourses } from '@neuroforge/shared/client'
import { useQuery } from '@tanstack/react-query'

import { useAcademySessionScope } from '@/lib/academy-session'
import { fetchCourses, fetchCourseBySlug, fetchCourseNavData } from '@/lib/api-academy'

const courseKeys = {
  all: ['courses'] as const,
  lists: () => [...courseKeys.all, 'list'] as const,
  list: (scope: string, filters: Record<string, unknown>) =>
    [...courseKeys.lists(), scope, filters] as const,
  details: () => [...courseKeys.all, 'detail'] as const,
  detail: (scope: string, slug: string) => [...courseKeys.details(), scope, slug] as const,
  nav: () => [...courseKeys.all, 'nav'] as const,
  stats: () => [...courseKeys.all, 'stats'] as const,
}

// ─── Liste cours (API REST) ───
export function useCourseList(
  params: {
    difficulty?: number
    tag?: string
    page?: number
    limit?: number
    sort?: 'order' | 'recent' | 'difficulty'
  } = {},
) {
  const scope = useAcademySessionScope()
  return useQuery({
    queryKey: courseKeys.list(scope, params),
    queryFn: ({ signal }) => fetchCourses(params, signal),
    staleTime: 5 * 60 * 1000,
  })
}

// ─── Recherche MeiliSearch ───
export function useCourseSearch(
  query: string,
  filters: {
    difficulty?: string
  } = {},
) {
  const meiliFilters: string[] = []
  if (filters.difficulty) meiliFilters.push(`difficulty = ${filters.difficulty}`)

  const scope = useAcademySessionScope()
  return useQuery({
    queryKey: [...courseKeys.all, 'search', scope, query, filters],
    queryFn: () =>
      searchCourses(query, {
        filter: meiliFilters.length > 0 ? meiliFilters.join(' AND ') : undefined,
        limit: 50,
      }),
    enabled: query.trim().length > 0,
    staleTime: 30_000,
  })
}

// ─── Détail cours par slug ───
export function useCourseDetail(slug: string | undefined) {
  const scope = useAcademySessionScope()
  return useQuery({
    queryKey: courseKeys.detail(scope, slug ?? ''),
    queryFn: ({ signal }) => fetchCourseBySlug(slug!, signal),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  })
}

// ─── Navigation sidebar (cours + modules + leçons + progression) ───
export function useCourseNav() {
  const scope = useAcademySessionScope()
  return useQuery({
    queryKey: [...courseKeys.nav(), scope],
    queryFn: ({ signal }) => fetchCourseNavData(signal),
    staleTime: 2 * 60 * 1000,
  })
}

// ─── Stats globales ───
export function useCourseStats() {
  const scope = useAcademySessionScope()
  const query = useQuery({
    queryKey: [...courseKeys.stats(), scope],
    queryFn: ({ signal }) => fetchCourses({ limit: 100, sort: 'order' }, signal),
    staleTime: 5 * 60 * 1000,
  })

  const courses = query.data?.data ?? []
  const totalCourses = query.data?.meta?.total ?? courses.length
  const totalLessons = courses.reduce((sum, c) => sum + c.totalLessons, 0)
  const totalMinutes = courses.reduce((sum, c) => sum + c.estimatedMinutes, 0)

  return { totalCourses, totalLessons, totalMinutes, isLoading: query.isLoading }
}
